import React, { useEffect } from "react"
import { Link } from "react-router-dom"
import { Container } from "reactstrap"
import SoldxHeader from "../../../../components/SoldxHeader"
import SoldxLandingFooter from "../../../../components/SoldxLandingFooter"
import { SupportPhonePair } from "../../../../components/SupportContactPhones"
import {
  SUPPORT_COMPANY_LEGAL_NAME,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO,
} from "../../../../config/supportContact"
import "../TermsAndConditions.scss"

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | SoldX"
  }, [])

  return (
    <div className="soldx-landing soldx-legal-page">
      <SoldxHeader solid />
      <main className="soldx-legal-main">
        <Container className="soldx-legal-container">
          <div className="soldx-legal-back-wrap">
            <Link to="/" className="soldx-legal-back">
              <i className="mdi mdi-arrow-left me-1" aria-hidden />
              Back to home
            </Link>
          </div>

          <div className="soldx-legal-card">
            <header className="soldx-legal-card-header">
              <h1 className="soldx-legal-card-title">Privacy Policy</h1>
            </header>
            <div className="soldx-legal-card-body">
              <div className="soldx-legal-dates" aria-label="Document dates">
                <span className="soldx-legal-dates-item">
                  <strong>Effective Date:</strong> 01/09/2025
                </span>
                <span className="soldx-legal-dates-item soldx-legal-dates-item--end">
                  <strong>Last Updated:</strong> 19/09/2025
                </span>
              </div>

              <div className="soldx-legal-content">
                <section className="soldx-legal-section">
                  <h2>Introduction</h2>
                  <p>
                    This Privacy Policy describes how SoldX India Pvt Ltd (&quot;SoldX&quot;, &quot;we&quot;,
                    &quot;our&quot;, or &quot;us&quot;) collects, uses, discloses, and protects your personal
                    information when you use our website, mobile applications, and related services (&quot;the
                    Platform&quot;). By using SoldX, you agree to this Privacy Policy. If you do not agree, please do
                    not use our Platform.
                  </p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Information We Collect</h2>
                  <p>
                    <strong>a) Information You Provide:</strong>
                  </p>
                  <ul>
                    <li>Name, phone number, email address, postal address.</li>
                    <li>
                      Identity proof and verification documents (e.g., Aadhaar, PAN, RERA certificate for brokers).
                    </li>
                    <li>Payment details for deposits, fees, and services.</li>
                    <li>Property-related details when creating a listing.</li>
                  </ul>
                  <p>
                    <strong>b) Information We Collect Automatically:</strong>
                  </p>
                  <ul>
                    <li>Device details (IP address, browser type, operating system).</li>
                    <li>Usage data (pages visited, time spent, actions).</li>
                    <li>Location data (with consent).</li>
                  </ul>
                  <p>
                    <strong>c) Information from Third Parties:</strong>
                  </p>
                  <ul>
                    <li>Verification from government databases.</li>
                    <li>Broker and seller details.</li>
                    <li>Creditworthiness checks.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>How We Use Your Information</h2>
                  <ul>
                    <li>Facilitate property listings, auctions, and transactions.</li>
                    <li>Verify identity and ensure compliance with applicable laws.</li>
                    <li>Provide analytics to sellers and brokers (clicks, views, reach, traffic).</li>
                    <li>Offer 24/7 buyer support and dispute assistance.</li>
                    <li>Send updates, notifications, and promotional offers.</li>
                    <li>
                      Provide optional legal, financial, and tax advisory services (on a paid basis).
                    </li>
                    <li>Improve platform performance, fraud detection, and security.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Sharing of Information</h2>
                  <ul>
                    <li>
                      <strong>Sellers and Brokers:</strong> Limited buyer information relevant to property
                      transactions.
                    </li>
                    <li>Payment partners for secure processing.</li>
                    <li>
                      <strong>Legal &amp; Advisory Partners:</strong> If you opt for advisory services.
                    </li>
                    <li>
                      <strong>Government/Regulators:</strong> To comply with RERA, IT Act, or other legal obligations.
                    </li>
                    <li>
                      <strong>Third-Party Service Providers:</strong> For hosting, analytics, marketing, and
                      verification services.
                    </li>
                  </ul>
                  <p>We do not sell your personal information to third parties.</p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Data Retention</h2>
                  <ul>
                    <li>We retain your data as long as your account is active or as required by law.</li>
                    <li>Transactional data may be kept longer for compliance and dispute resolution.</li>
                    <li>Data deletion requests are honored as per regulatory requirements.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Cookies &amp; Tracking Technologies</h2>
                  <p>
                    SoldX uses cookies and similar technologies to improve user experience, personalize content, and
                    analyze usage.
                  </p>
                  <p>You can manage cookie preferences in your browser settings.</p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Data Security</h2>
                  <p>
                    We implement industry-standard encryption, firewalls, and access controls to protect your data.
                  </p>
                  <p>
                    While we take all precautions, no system is 100% secure. Users share information at their own risk.
                  </p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Your Rights</h2>
                  <ul>
                    <li>Access, update, or correct personal information.</li>
                    <li>Request data deletion, subject to legal compliance.</li>
                    <li>Opt-out of marketing communications.</li>
                    <li>Withdraw consent for location and cookies.</li>
                  </ul>
                  <p>
                    Make requests via email:{" "}
                    <a href={SUPPORT_MAILTO} className="soldx-legal-link">
                      {SUPPORT_EMAIL}
                    </a>
                    .
                  </p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Children&apos;s Privacy</h2>
                  <p>SoldX services are intended for individuals 18 years and older.</p>
                  <p>We do not knowingly collect data from minors.</p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Changes to This Privacy Policy</h2>
                  <p>
                    SoldX may update this Privacy Policy. Updates will be posted on our website with the revised date.
                  </p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Governing Law</h2>
                  <p>This Privacy Policy is governed by the laws of India.</p>
                  <p>Disputes are subject to the jurisdiction of courts in Haryana.</p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Contact Us</h2>
                  <p>For privacy-related queries, contact:</p>
                  <p className="soldx-legal-contact-block">
                    <strong>{SUPPORT_COMPANY_LEGAL_NAME}</strong>
                    <br />
                    Email:{" "}
                    <a href={SUPPORT_MAILTO} className="soldx-legal-link">
                      {SUPPORT_EMAIL}
                    </a>
                    <br />
                    Phone:{" "}
                    <SupportPhonePair linkClassName="soldx-legal-link" />
                  </p>
                </section>
              </div>
            </div>
          </div>
        </Container>
      </main>

      <SoldxLandingFooter />
    </div>
  )
}

export default PrivacyPolicy
